/**
 * Readable colours for the widget. A business picks its brand colour for the
 * button and accents; used as-is for text it is often unreadable — IITA's blue
 * on the dark answer bubble measured 1.78:1, gold on white 2.9:1. WCAG AA asks
 * 4.5:1 for body-size text. React-free.
 */

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

type Rgb = [number, number, number];

function parseHex(color: string): Rgb | null {
  const match = HEX.exec(color.trim());
  if (!match) return null;
  const hex = match[1].length === 3 ? [...match[1]].map((c) => c + c).join('') : match[1];
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

function luminance([r, g, b]: Rgb): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two hex colours (1–21); 1 when either can't be parsed. */
export function contrastRatio(a: string, b: string): number {
  const ra = parseHex(a);
  const rb = parseHex(b);
  if (!ra || !rb) return 1;
  const [hi, lo] = [luminance(ra), luminance(rb)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * `color` as text on every one of `backgrounds`: unchanged when it already
 * reaches `min`, otherwise mixed toward white (dark backgrounds) or black
 * (light ones) just far enough — so it stays recognisably the brand's colour.
 * Colours that aren't hex (a named colour) are returned as-is.
 */
export function readableOn(color: string, backgrounds: string[], min = 4.5): string {
  const rgb = parseHex(color);
  const bgs = backgrounds.map(parseHex).filter((bg): bg is Rgb => bg !== null);
  if (!rgb || bgs.length === 0) return color;
  const worst = (c: Rgb) => Math.min(...bgs.map((bg) => contrastRatio(toHex(c), toHex(bg))));
  if (worst(rgb) >= min) return toHex(rgb);
  const darkBackground = bgs.reduce((sum, bg) => sum + luminance(bg), 0) / bgs.length < 0.18;
  const target: Rgb = darkBackground ? [255, 255, 255] : [0, 0, 0];
  for (let step = 1; step <= 20; step++) {
    const t = step / 20;
    const mixed = rgb.map((v, i) => v + (target[i] - v) * t) as Rgb;
    if (worst(mixed) >= min) return toHex(mixed);
  }
  return toHex(target);
}
